import React, { useState, useEffect } from 'react';
import { FaEye, FaHandPaper, FaVolumeUp, FaFlask, FaUtensils } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import ModuleHeader from "../../moduleHeader";

const defaultPackageData = {
  user: {
    first_name: "Kullanıcı",
    last_name: ""
  },
  package: {
    name: "Grounding Technique",
    description: "Anksiyete ve stres anlarında kendinizi şimdiki ana getirmek için 5-4-3-2-1 tekniğini kullanın. Beş duyunuzu kullanarak çevrenizle bağlantı kurun.",
    image_url: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80"
  }
};

const steps = [
  { number: 5, sense: "gör", instruction: "Etrafınızda 5 şey görün", icon: FaEye },
  { number: 4, sense: "dokun", instruction: "4 şeye dokunun", icon: FaHandPaper },
  { number: 3, sense: "duy", instruction: "3 ses duyun", icon: FaVolumeUp },
  { number: 2, sense: "kokla", instruction: "2 şeyin kokusunu fark edin", icon: FaFlask },
  { number: 1, sense: "tat", instruction: "1 şeyin tadını düşünün", icon: FaUtensils }
];

export default function GroundingTechnique({ id, moduleId, isOwner }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [userInputs, setUserInputs] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [packageData] = useState(defaultPackageData);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  const handleInputChange = (stepNumber, value) => {
    setUserInputs(prev => ({
      ...prev,
      [stepNumber]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setIsCompleted(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setUserInputs({});
    setIsCompleted(false);
  };

  const currentStepData = steps[currentStep];

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        <div className="relative z-30 w-full mb-2">
          <ModuleHeader
            userName={packageData.user?.first_name + " " + packageData.user?.last_name}
            subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
            title={packageData.package?.name}
            description={packageData.package?.description}
            backgroundImage={packageData.package?.image_url}
            onExpandedChange={setIsHeaderExpanded}
            showToggle={true}
            autoHeight={true}
          />
        </div>

        <div className="w-full px-6 py-8 pb-24 flex-1">
          {!isCompleted ? (
            <>
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400 font-medium">
                    {currentStep + 1} / {steps.length}
                  </span>
                  <span className="text-xs text-gray-400 font-medium">
                    {Math.round(((currentStep + 1) / steps.length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1">
                  <div 
                    className="bg-[#3DA7B5] h-1 rounded-full transition-all duration-500"
                    style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Current Step Card */}
              <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-6">
                <div className="text-center mb-8">
                  <div className="flex justify-center mb-6">
                    {(() => {
                      const IconComponent = currentStepData.icon;
                      return <IconComponent className="text-6xl text-[#3DA7B5]" />;
                    })()}
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                    {currentStepData.number} Şey {currentStepData.sense.charAt(0).toUpperCase() + currentStepData.sense.slice(1)}
                  </h2>
                  <p className="text-gray-500 text-sm">{currentStepData.instruction}</p>
                </div>

                <div className="space-y-3">
                  <label className="block text-xs text-gray-400 font-medium mb-2">
                    Yazın
                  </label>
                  <textarea
                    value={userInputs[currentStepData.number] || ''}
                    onChange={(e) => handleInputChange(currentStepData.number, e.target.value)}
                    placeholder={`Örnek: ${currentStepData.sense === 'gör' ? 'Mavi gökyüzü, yeşil ağaç, beyaz bulut...' : currentStepData.sense === 'dokun' ? 'Yumuşak kumaş, pürüzsüz cam, soğuk metal...' : currentStepData.sense === 'duy' ? 'Kuş sesi, rüzgar sesi, araba sesi...' : currentStepData.sense === 'kokla' ? 'Kahve kokusu, çiçek kokusu...' : 'Çikolata tadı...'}`}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-[#3DA7B5] resize-none text-sm"
                    rows="5"
                  />
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-2">
                {currentStep > 0 && (
                  <button
                    onClick={handleBack}
                    className="flex-1 bg-white border border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-700 rounded-full py-3 font-medium text-sm transition-all duration-300"
                  >
                    Geri
                  </button>
                )}
                <button
                  onClick={handleNext}
                  disabled={!userInputs[currentStepData.number]?.trim()}
                  className={`flex-1 bg-[#3DA7B5] text-white rounded-full py-3 font-medium text-sm transition-all duration-300 ${
                    !userInputs[currentStepData.number]?.trim() 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-[#2F8894]'
                  }`}
                >
                  {currentStep === steps.length - 1 ? 'Tamamla' : 'İleri'}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center animate-fadeIn">
              <div className="flex justify-center mb-8">
                <HiSparkles className="text-6xl text-[#3DA7B5]" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">Teknik Tamamlandı</h2>
              <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                Şimdi kendinizi daha sakin ve şimdiki anda hissetmelisiniz. Bu tekniği ihtiyaç duyduğunuz her an kullanabilirsiniz.
              </p>
              
              <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
                <h3 className="text-sm font-medium text-gray-400 mb-4 text-left">Tamamladıklarınız</h3>
                <div className="space-y-4 text-left">
                  {steps.map((step, index) => {
                    const IconComponent = step.icon;
                    return (
                    <div key={index} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                      <IconComponent className="text-xl text-[#3DA7B5] mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-700 text-sm mb-1">{step.instruction}</p>
                        <p className="text-xs text-gray-400 leading-relaxed">{userInputs[step.number] || 'Boş'}</p>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>

              <button
                onClick={handleRestart}
                className="w-full bg-[#3DA7B5] hover:bg-[#2F8894] text-white rounded-full py-4 font-medium text-base transition-all duration-300"
              >
                Tekrar Başla
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

